/*
 * Copyright (c) Akveo 2019. All Rights Reserved.
 * Licensed under the Single Application / Multi Application License.
 * See LICENSE_SINGLE_APP / LICENSE_MULTI_APP in the 'docs' folder for license information on type of purchased license.
 */

import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { NbAuthService } from '@nebular/auth';
import { NEVER, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Result of trying to renew the session after a 401:
 *  - refreshed: a new access token is in storage, the failed request can be replayed.
 *  - rejected:  the API refused the refresh token (or there is none) - the session is really over.
 *  - transient: the refresh could not be completed (network, 5xx, timeout, ...) - says nothing about
 *               the session, so the user must NOT be logged out for it.
 */
type RefreshOutcome = 'refreshed' | 'rejected' | 'transient';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  // Concurrent 401s share one refresh call instead of each firing their own.
  private refreshInFlight$: Observable<RefreshOutcome> | null = null;
  private lastLogoutAt = 0;

  // NbAuthService is resolved lazily (as NbAuthJWTInterceptor does): it depends on HttpClient,
  // which depends on the interceptors, so injecting it directly would be circular.
  constructor(private router: Router, private injector: Injector) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (req.url.includes('/tenants/null/tenantmst/null')) {
      return NEVER;
    }

    // The page-access lookup is built from localStorage (tenantId / userType). With an empty session
    // the tenant becomes the literal 'null' and the API can only answer 400, so fail here with the
    // same error shape instead of sending it - each caller's error handler runs exactly as before.
    if (/\/userrole\/(null|undefined)\/[^/]+\/pageaccesssearch/i.test(req.url)) {
      return throwError(new HttpErrorResponse({
        status: 400,
        statusText: 'Bad Request',
        url: req.url,
        error: 'Tenant id is missing - session data is not available.'
      }));
    }

    // Appointment slot lookups are built from localStorage (tenantId / branchId). With an empty session
    // either one becomes the literal 'null' / 'undefined' in the URL and the request has nothing valid to
    // search with, so fail here with the same error shape instead of sending it.
    if (/\/patappointment\/(null|undefined)\/[^/]+\/getdoctorslotsforappointment/i.test(req.url)
        || /\/patappointment\/[^/]+\/(null|undefined)\/getdoctorslotsforappointment/i.test(req.url)) {
      return throwError(new HttpErrorResponse({
        status: 400,
        statusText: 'Bad Request',
        url: req.url,
        error: 'Tenant or branch id is missing - session data is not available.'
      }));
    }

    // Patient lookup by id (GET patientregistrationmst/{tenantId}/{branchId}/{id}). With no patient selected
    // the id becomes the literal 'null' / 'undefined', which the API cannot bind to a Guid, so fail here.
    if (req.method === 'GET' && /\/patientregistrationmst\/[^/]+\/[^/]+\/(null|undefined)\/?(\?.*)?$/i.test(req.url)) {
      return throwError(new HttpErrorResponse({
        status: 400,
        statusText: 'Bad Request',
        url: req.url,
        error: 'Patient id is missing.'
      }));
    }

    const token = JSON.parse(localStorage.getItem("loginresult"))?.response?.body?.token?.access_token;

    // NbAuthJWTInterceptor runs first and attaches the current (silently refreshed) token.
    // "loginresult" is only written at login, so after the 60-min access token expires it
    // holds a stale token - overwriting Nebular's header with it caused a 401 and a forced
    // logout ~1 hour after login. Only fall back to it when no header was set.
    let authReq = req;
    if (token && !req.headers.has('Authorization')) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // A 401 from the login endpoint itself just means "wrong credentials" on this
        // attempt - the login component's own NbAuthResult.isSuccess() branch already shows
        // that inline. Treating it here too as "session expired" cleared localStorage
        // (WellSpringAppFlag/tenantAlias/SecondOpinionFlag/employeeRoleUrlSuffix/etc.) out from
        // under a still-mounted login page, so a valid retry right after would read a
        // wiped SecondOpinionFlag and land on the wrong dashboard (or this generic
        // auth/login) - skip the global-logout handling entirely for this endpoint.
        //
        // Only a 401 from our own API says anything about our session: a 401 from another host
        // (e.g. the ABHA service reporting that its own OTP session expired) is that caller's
        // business and must not end the HMS session. Before ending the session, try to renew it -
        // an expired access token with a still-valid refresh token is not a reason to log out.
        if (error.status === 401 && !req.url.includes('/auth/login') && !req.url.includes('/auth/refresh-token')
            && !this.router.url.startsWith('/auth/well-spring-login') && this.isOwnApi(req.url)) {
          return this.recoverFromUnauthorized(req, next, error);
        }

        // TODO: handle 403 errors if needed
        return throwError(error);
      })
    );
  }

  /**
   * 401 from our API: renew the access token once (shared by all concurrent 401s) and replay the
   * request. Log out only when the session is really gone - the refresh token was refused, or the
   * replayed request is refused again. A refresh that merely failed to complete keeps the session.
   */
  private recoverFromUnauthorized(req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    return this.refreshOnce().pipe(
      switchMap((outcome: RefreshOutcome) => {
        if (outcome === 'refreshed') {
          return this.replayWithFreshToken(req, next).pipe(
            catchError((retryError: HttpErrorResponse) => {
              if (retryError.status === 401) {
                this.endSession();
              }
              return throwError(retryError);
            })
          );
        }
        if (outcome === 'rejected') {
          this.endSession();
        }
        return throwError(error);
      })
    );
  }

  private refreshOnce(): Observable<RefreshOutcome> {
    if (!this.refreshInFlight$) {
      const authService = this.injector.get(NbAuthService);
      this.refreshInFlight$ = authService.getToken().pipe(
        take(1),
        switchMap(token => {
          if (!token || !token.getValue() || !token.getOwnerStrategyName()) {
            return of<RefreshOutcome>('rejected'); // no session to renew
          }
          return authService.refreshToken(token.getOwnerStrategyName(), token).pipe(
            map((result): RefreshOutcome => {
              if (result.isSuccess()) {
                return 'refreshed';
              }
              // The API answers 400 for a refresh token it will not accept (see AuthController.RefreshToken).
              // Anything else (no response, 5xx, ...) is a failure to refresh, not a verdict on the session.
              const status = result.getResponse()?.status;
              return status === 400 || status === 401 || status === 403 ? 'rejected' : 'transient';
            })
          );
        }),
        catchError(() => of<RefreshOutcome>('transient')),
        finalize(() => { this.refreshInFlight$ = null; }),
        shareReplay(1)
      );
    }
    return this.refreshInFlight$;
  }

  private replayWithFreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.injector.get(NbAuthService).getToken().pipe(
      take(1),
      switchMap(token => next.handle(req.clone({
        setHeaders: {
          Authorization: `Bearer ${token.getValue()}`
        }
      })))
    );
  }

  /** A relative URL, or one on the same host as the HMS API (apiUrl / baseUrl). */
  private isOwnApi(url: string): boolean {
    const origin = AuthInterceptor.originOf(url);
    if (origin === null) {
      return true;
    }
    return [environment.apiUrl, environment.baseUrl].some(base => !!base && AuthInterceptor.originOf(base) === origin);
  }

  private static originOf(url: string): string | null {
    const match = /^(?:https?:)?\/\/[^/?#]+/i.exec(url);
    return match ? match[0].replace(/^https?:/i, '').toLowerCase() : null;
  }

  private endSession(): void {
    // Concurrent 401s all end up here; only the first sees the session data the redirect depends on.
    if (Date.now() - this.lastLogoutAt < 3000) {
      return;
    }
    this.lastLogoutAt = Date.now();

    const isPatient = localStorage.getItem("loginuser") === "patientlogin";
    const userId = localStorage.getItem("wardUserId");
    const priceList = localStorage.getItem("wardWisePrice");
    const isInsurance =localStorage.getItem("InsuranceFlag") === "true"?true:false;
    const type = localStorage.getItem('PatloginUserType');
    // WellSpring/"Second Opinion" sessions (Patient/Doctor/Care Navigator/Reviewer) also
    // set InsuranceFlag=true post-login but never PatloginUserType - captured before the
    // clear() below so an expired WellSpring session bounces back to its own
    // well-spring-login instead of this generic HMS auth/login (or the broken
    // auth/care-sphere/null a bare isInsurance check would produce).
    const isWellSpring = localStorage.getItem('WellSpringAppFlag') === 'true' && !type;
    const tenantAlias = localStorage.getItem('tenantAlias');
    const employeeRoleUrlSuffix = localStorage.getItem('employeeRoleUrlSuffix');

    localStorage.clear();
    localStorage.setItem("wardWisePrice", priceList || '');
    localStorage.setItem("wardUserId", userId || '');

    if (isWellSpring) {
      if (tenantAlias && employeeRoleUrlSuffix) {
        this.router.navigate([`auth/well-spring-login/${tenantAlias}/${employeeRoleUrlSuffix}`]);
      } else if (tenantAlias) {
        this.router.navigate([`auth/well-spring-login/${tenantAlias}`]);
      } else {
        this.router.navigate(['auth/well-spring-login']);
      }
    }
    else if (isPatient && !isInsurance) {
      this.router.navigate(['auth/patient-portal']);
    }
    else if(isInsurance){
      this.router.navigate([`auth/care-sphere/${type}`]);
    }
    else {
      this.router.navigate(['auth/login']);
    }
  }
}
